'use client'

import { useEffect, useState } from 'react'
import useSWR from 'swr'
import apiService from '@/app/services/apiService'
import { Loader2, TicketCheck } from 'lucide-react'
import { QRCodeCanvas } from 'qrcode.react'
import { useSidebarStore } from '../hooks/sidebarStore'

type Ticket = {
  id: string
  code: string
  product: {
    id: string
    title: string
    image: string
    price: number
  }
  quantity: number
  total_price: number
  purchased_at: string
  is_used: boolean
}

export default function MyTicketsPage() {

  const emptyImages = [
    "/images/sponsors/apoio-local1.png",
    "/images/sponsors/apoio-local4.png",
    "/images/sponsors/apoio-local5.png",
    "/images/sponsors/apoio-local7.png",
    "/images/sponsors/brahma-seeklogo.png",
    "/images/sponsors/heineken-white.png",
    "/images/sponsors/nubank-white.png",
    "/images/sponsors/Itaip-bier.png",
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  
  useEffect(() => {
      const interval = setInterval(() => {
          setCurrentIndex((prevIndex) => (prevIndex + 1) % emptyImages.length);
      }, 3000); // troca a cada 4 segundos

      return () => clearInterval(interval);
  }, []);

  const { data: tickets, error, isLoading } = useSWR<Ticket[]>(
    '/api/shops/tickets/my/',
    (url: string) => apiService.getWithToken(url)
  )

  const formatCurrency = (value: number | string) => {
    return `R$ ${Number(value).toFixed(2)}`;
  }

  const { isCollapsed } = useSidebarStore()

  if (isLoading) {
    return (
      <div className={`min-h-screen flex justify-center items-center h-64 p-6 transition-all duration-300 ${isCollapsed ? 'md:ml-16' : 'md:ml-64'}`}>
        <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
        <span className="ml-2">Carregando ingressos...</span>
      </div>
    )
  }

  if (error || !tickets) {
    return (
      <div className={`text-center text-red-600 mt-10 p-6 transition-all duration-300 ${isCollapsed ? 'md:ml-16' : 'md:ml-64'}`}>
        Ocorreu um erro ao carregar seus ingressos.
      </div>
    )
  }

  if (tickets.length === 0) {
    return (
      <>
      <div className={`min-h-screen text-center text-gray-600 mt-10 p-6 transition-all duration-300 ${isCollapsed ? 'md:ml-16' : 'md:ml-64'}`}>
        Nenhum ticket encontrado.
        <div className="col-span-full flex flex-col items-center justify-center mt-12 text-center">
            <img
                src={emptyImages[currentIndex]}
                alt="Sem tickets"
                className="w-72 h-72 md:w-96 md:h-96 rounded-2xl shadow-xl object-contain mb-6 transition-opacity duration-700 ease-in-out"
            />
        </div>

        <p className="text-gray-600 mb-6">
          Você ainda não realizou compras de tickets em nossa plataforma. Compre agora!
        </p>
        <a
          href="/shop"
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition"
        >
          Comprar tickets
        </a>
      </div>
      </>
    )
  }

  return (
    <div className={`max-w[1500px] mx-auto min-h-screen px-6 p-6 ml-0 transition-all duration-300 ${isCollapsed ? 'md:ml-16' : 'md:ml-64'}`}>
      <h1 className="text-2xl font-bold mb-8 flex items-center gap-2">
        <TicketCheck className="w-6 h-6" /> Meus Ingressos
      </h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {tickets.map((ticket) => (
          <div
            key={ticket.id}
            className={`bg-white border rounded-2xl shadow-sm overflow-hidden ${
              ticket.is_used ? 'opacity-60' : ''
            }`}
          >
            <img
              src={ticket.product.image}
              alt={ticket.product.title}
              className="w-full h-48 object-cover"
            />
            <div className="p-4 space-y-2">
              <h2 className="text-lg font-semibold">{ticket.product.title}</h2>
              <p>💰 Total: {formatCurrency(ticket.total_price)}</p>
              <p className="text-sm text-gray-500">
                🕒 Comprado em:{' '}
                {new Date(ticket.purchased_at).toLocaleDateString('pt-BR')}
              </p>

              <div className="flex justify-center">
                <QRCodeCanvas
                  value={ticket.code}
                  size={120}
                  bgColor={'#ffffff'}
                  fgColor={'#000000'}
                  level={'H'}
                  includeMargin={false}
                />
              </div>

              {ticket.is_used ? (
                <p className="text-sm text-red-600 font-semibold text-center">
                  ❌ Ingresso já utilizado
                </p>
              ) : (
                <p className="text-sm text-green-600 font-semibold text-center">
                  ✅ Ingresso válido
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
